import {Component, forwardRef, inject, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import { CommonModule } from '@angular/common';
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {
  IActionMapping,
  ITreeOptions,
  ITreeState,
  KEYS,
  TreeModel,
  TreeModule,
  TreeNode
} from "@ali-hm/angular-tree-component";
import {tap, timer} from "rxjs";
import {OrganisationService} from "../../../core/services/organisation/organisation.service";
import {Organisation} from "../../../core/models/organisation.model";
import {ITreeNode} from "@ali-hm/angular-tree-component/lib/defs/api";
import {OrganisationNameComponent} from "../organisation-name/organisation-name.component";
import {localeCompareDK} from "../../../core/utilities/string.utils";

@Component({
  selector: 'app-organisation-selector',
  standalone: true,
  imports: [CommonModule, TreeModule,
    forwardRef(() => OrganisationNameComponent)
  ],
  template: `
      <div class="modal-header">
        <h5 class="modal-title">
          {{ singleSelect ? 'Vælg en organisation' : 'Vælg organisationer' }}
        </h5>
        <button type="button" class="btn-close" (click)="modal.close()" aria-label="Luk"></button>
      </div>
      <div class="modal-body">
        <tree-root [nodes]="organisationTree" #treeComponent [options]="options" [ngClass]="'tree tree-show-expander'"
                   [(state)]="state"
                   (select)="onSelect($event)"
                   (deselect)="onDeselect($event)"
                   >
          <ng-template #treeNodeExpanderTemplate let-node>
            <span class="icon-folder">dddd</span>dd
          </ng-template>
          <ng-template #treeNodeTemplate let-node let-index="index">
            <div class="tree-node-content my-1">
                {{node.data.name}}
            </div>
          </ng-template>
        </tree-root>
      </div>
      <div class="modal-footer">
        <div class="float-start me-auto" *ngIf="singleSelect">
          Valgt organisation: <app-organisation-name [id]="getSingleSelected()" [showPath]="true"></app-organisation-name>
        </div>
        <button type="button" class="float-end btn btn-outline-secondary" (click)="closeWithNoCollection()" *ngIf="allowEmpty">Intet valg</button>
        <button type="button" class="float-end btn btn-secondary" (click)="closeWithChosen()">Vælg</button>
      </div>
  `,
  styles: [`
    .modal-body {
      max-height: calc(100vh - 200px);
      overflow-y: auto;
    }
  `,
  ]
})
export class OrganisationSelectorComponent implements OnInit {
  modal = inject(NgbActiveModal);
  private organisationService = inject(OrganisationService);
  state: ITreeState = {};
  public selectedIds: string[] = [];
  public singleSelect = false;
  public selectPublishers = false;
  public allowEmpty = false;
  @ViewChild('treeComponent') treeComponent: TreeNode;

  ngOnInit(): void {
    this.modal.update({size: 'lg'});
    if (this.selectedIds) {
      this.state = {
        ...this.state,
        selectedLeafNodeIds: this.selectedIds.reduce((acc, id) => ({...acc, [id]: true}), {})
      };

    }
    let orgSource = this.organisationService.organisations$;
    if (this.selectPublishers) {
      orgSource = this.organisationService.publishers$;
    }

    orgSource.subscribe(orgs => {
      this.organisationTree = this.buildTree(orgs);

      setTimeout(() => {
        this.selectedIds.forEach(id => {
          console.log('Activating node with id:', id);
          this.treeComponent.treeModel.getNodeById(id)?.setActiveAndVisible(true);
        });
      }, 100)
    });

    if (this.singleSelect) {
      this.options.useCheckbox = false;
    }
  }

  close(): void {
    this.modal.close();
  }

  options: ITreeOptions = {
    allowDrag: (node: any) => false,
    allowDrop: (node: any) => false,
    allowDragoverStyling: false,
    levelPadding: 10,
    // useVirtualScroll: true,
    animateExpand: true,
    idField: 'id',
    displayField: 'name',
    childrenField: 'children',
    isExpandedField: 'isExpanded',
    scrollOnActivate: true,
    useCheckbox:  true,
    animateSpeed: 30,
    animateAcceleration: 1.2,
    useTriState: false,
    scrollContainer: document.documentElement, // HTML
    actionMapping: {
      mouse: {
        click: (tree: TreeModel, node: ITreeNode, $event: any) => {
          this.handleClick(node);
        },
        dblClick: (tree: TreeModel, node: ITreeNode, $event: any) => {
          this.handleClick(node);
          if (this.singleSelect) {
            this.closeWithChosen();
          }
        }
      },
      keys: {
        [KEYS.ENTER]: (tree: TreeModel, node: ITreeNode, $event: any) => {
          this.handleClick(node);
        }
      }
    } as IActionMapping,
  };
  organisationTree: Partial<ITreeNode>[] = [];

  private handleClick(node: ITreeNode) {
    if (node.hasChildren) {
      node.toggleExpanded();
    }

    node.toggleActivated(false);

    if (this.singleSelect) {
      if (node.isActive) {
        this.selectedIds = [node.id as string];
        return;
      }

      this.selectedIds = [];
      return;
    }

  }

  private buildTree(orgs: Organisation[]): Partial<ITreeNode>[] {
    const hashTable = Object.create(null);
    orgs.forEach(org => {hashTable[org.id] = {...org, children: []}});
    const dataTree: { id: string, name: string, children: any[] }[] = [];
    orgs.forEach(aData => {
      if(aData.parentId && hashTable[aData.parentId]?.children) {
        hashTable[aData.parentId].children.push(hashTable[aData.id])
        return;
      }

      dataTree.push(hashTable[aData.id])
    });

    // sort tree
    dataTree.sort((a, b) => localeCompareDK(a.name, b.name));
    dataTree.forEach(node => node.children.sort((a, b) => localeCompareDK(a.name, b.name)));

    return dataTree as Partial<ITreeNode>[];
  }

  closeWithChosen() {
    return this.modal.close(this.selectedIds);
  }

  getSingleSelected(): string {
    return this.selectedIds[0] ?? '';
  }

  onSelect(event: {node: TreeNode}) {
    this.selectedIds.push(event.node.id);
  }

  onDeselect(event: {node: TreeNode}) {
    this.selectedIds = this.selectedIds.filter(id => id !== event.node.id);
  }

  closeWithNoCollection() {
    return this.modal.close([]);
  }
}
